import {
  RouteEntitySubscriberService,
  SignalTopicBinding,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemMapping,
  ClientCreditNoteDraftItem,
  ClientCreditNoteItem,
  findSectionIriByEntityIri,
} from 'shared';
import { SupplierCreditNoteCoordinator } from '../SupplierCreditNoteCoordinator/supplier-credit-note.coordinator';

const supplierCreditNoteRefreshPlanBuilder = (args: {
  tracker: RouteEntitySubscriberService;
  coordinator: SupplierCreditNoteCoordinator;
}): SignalTopicBinding => {
  const { tracker, coordinator } = args;

  const itemsBySectionSig = tracker.getSectionItemsSignal<SupplierCreditNoteItem>('supplierCreditNote');
  const mappingsBySectionSig = tracker.getItemMappingsSignal<SupplierCreditNoteItemMapping>('supplierCreditNote');
  const draftsBySectionSig = tracker.getDraftItemsSignal<ClientCreditNoteDraftItem>('supplierCreditNote');
  const linkedBySectionSig = tracker.getLinkedItemsSignal<ClientCreditNoteItem>('supplierCreditNote');

  // --- resolvers ---
  const sectionByItem = (itemIri: string): string | undefined => {
    return findSectionIriByEntityIri(itemsBySectionSig(), itemIri) ?? undefined;
  };

  const sectionByMapping = (mappingIri: string): string | undefined => {
    const bySection = mappingsBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as SupplierCreditNoteItemMapping[]) ?? []).some((m) => m['@id'] === mappingIri)) return secIri;
    }
    return undefined;
  };

  const sectionByDraft = (draftIri: string): string | undefined => {
    const bySection = draftsBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as ClientCreditNoteDraftItem[]) ?? []).some((d) => d['@id'] === draftIri)) return secIri;
    }
    return undefined;
  };

  const sectionByLinked = (linkedIri: string): string | undefined => {
    const bySection = linkedBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as ClientCreditNoteItem[]) ?? []).some((it) => it['@id'] === linkedIri)) return secIri;
    }
    return undefined;
  };

  // const draftItemsBySection = (topic: string): string => {
  //   // "/supplier_credit_note_sections/123/draft_items" -> "/supplier_credit_note_sections/123"
  //   return topic.replace(/\/draft_items$/, '');
  // }

  return {
    patterns: {
      entity: /\/supplier_credit_notes\/\d+$/,
      items: /\/supplier_credit_note_items\/\d+$/,
      itemMappings: /\/supplier_credit_note_item_mappings\/\d+$/,
      draftItems: /\/client_credit_note_draft_items\/\d+$/,
      linkedItems: /\/client_credit_note_items\/\d+$/,
    },
    resolvers: {
      sectionByItem,
      sectionByMapping,
      sectionByDraft,
      sectionByLinked,
    },
    refresh: {
      entity: () => coordinator.refreshCreditNoteEntity(),

      // item topic changed => refresh mappings for that line
      items: (_sectionIri: string, itemIri?: string) => {
        if (!itemIri) return;
        return coordinator.refreshItemMappingForLine(itemIri);
      },

      itemMappings: (_sectionIri: string, itemIri?: string) =>
        itemIri
          ? coordinator.refreshItemMappingForLine(itemIri)
          : coordinator.refreshSectionMappings(),

      draftItems: () => coordinator.refreshDraftItems(),
      linkedItems: () => coordinator.refreshLinkedItems(),
    }
  };
};

export { supplierCreditNoteRefreshPlanBuilder };
